import { useContext, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Table, Button, Space, Modal, Form, Input, InputNumber, DatePicker, Tag, Typography, Descriptions, Divider } from 'antd';
import MainCard from 'components/MainCard';
import {
  createUser,
  getUsersListByPagination,
  sendPasswordEmail,
  updateUserAccess,
  updateUserStatus,
} from '../../utils/api';
import { UserContext } from '../../contexts/UserContext';
import ModalConfirm from './ModalConfirm';
import { formatDate, formatToIST, getInspectionExpiryStatus } from '../../utils/helper';

export default function UserList() {
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { showToast } = useContext(UserContext);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isViewProfileModalOpen, setIsViewProfileModalOpen] = useState(false);
  const [isEditProfileModalOpen, setIsEditProfileModalOpen] = useState(false);
  const [selectedProfileUser, setSelectedProfileUser] = useState(null);
  const [createForm] = Form.useForm();
  const [accessForm] = Form.useForm();
  const [profileForm] = Form.useForm();

  const SUPPORT_EMAIL = 'support@millwize.com';

  const isAdminUser = (record) =>
    record?.role === 'admin' ||
    record?.user_type === 'admin' ||
    record?.type === 'admin' ||
    record?.is_admin === true ||
    record?.is_admin === 1;

  const getUserStatus = (record) => {
    const status = record?.status ?? record?.user_status ?? record?.to_status;
    return status === null || status === undefined ? 1 : status;
  };

  const getCountryCode = (record) =>
    record?.country_code ?? record?.dial_code ?? record?.phone_country_code ?? record?.mobile_country_code;

  const getPhone = (record) => record?.phone ?? record?.mobile ?? record?.phone_number ?? record?.mobile_number;

  const getAllowedEquipment = (record) =>
    record?.limits?.allowed_equipment ??
    record?.equipment_limit ??
    record?.allowed_equipment_count ??
    record?.no_of_equipment ??
    record?.equipment_count_limit;

  const getCurrentEquipmentCount = (record) =>
    record?.limits?.current_equipment ??
    record?.equipment_used_count ??
    record?.current_equipment_count ??
    record?.total_equipment ??
    record?.equipment_count ??
    record?.user_equipment_count;

  const getRemainingEquipmentSlots = (record) =>
    record?.limits?.remaining_equipment_slots ?? record?.remaining_equipment_slots;

  const getInspectionExpiryDate = (record) =>
    record?.limits?.inspection_expiry_date ??
    record?.inspection_expiry_date ??
    record?.expiry_date ??
    record?.inspection_expiry ??
    record?.subscription_expiry_date;

  const getInspectionExpiryLabel = (record) => {
    const limitsStatus = record?.limits?.inspection_expiry_status ?? record?.inspection_expiry_status;
    if (limitsStatus === 'not_set') {
      return { label: 'Not Set', color: 'default' };
    }

    return getInspectionExpiryStatus(getInspectionExpiryDate(record));
  };

  const fetchData = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const response = await getUsersListByPagination({ page, limit: pageSize });
      const result = response.data;

      setData(result.data);
      setPagination({
        current: result.current_page,
        pageSize: result.per_page,
        total: result.total
      });
    } catch (error) {
      console.error('Failed to fetch user list:', error);
      showToast('Failed to load users', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(pagination.current, pagination.pageSize);
  }, []);

  const handleTableChange = (nextPagination) => {
    fetchData(nextPagination.current, nextPagination.pageSize);
  };

  const showAddModal = () => {
    createForm.resetFields();
    setIsCreateModalOpen(true);
  };

  const openAccessModal = (record) => {
    const expiryDate = getInspectionExpiryDate(record);

    setSelectedUser(record);
    accessForm.setFieldsValue({
      equipment_limit: getAllowedEquipment(record),
      inspection_expiry_date: expiryDate ? dayjs(expiryDate) : null
    });
    setIsAccessModalOpen(true);
  };

  const handleCreateCancel = () => {
    setIsCreateModalOpen(false);
    createForm.resetFields();
  };

  const handleAccessCancel = () => {
    setIsAccessModalOpen(false);
    setSelectedUser(null);
    accessForm.resetFields();
  };

  const handleProfileCancel = () => {
    setIsViewProfileModalOpen(false);
    setIsEditProfileModalOpen(false);
    setSelectedProfileUser(null);
    profileForm.resetFields();
  };

  const openViewProfileModal = (record) => {
    setSelectedProfileUser(record);
    profileForm.setFieldsValue({
      email: record?.email,
      country_code: getCountryCode(record),
      phone: getPhone(record)
    });
    setIsViewProfileModalOpen(true);
  };

  const openEditProfileModal = (record) => {
    setSelectedProfileUser(record);
    profileForm.setFieldsValue({
      email: record?.email,
      country_code: getCountryCode(record),
      phone: getPhone(record)
    });
    setIsEditProfileModalOpen(true);
  };

  const handleResetPassword = async (record) => {
    if (!record?.email) {
      showToast('User email is missing', 'error');
      return;
    }

    setSubmitting(true);
    try {
      await sendPasswordEmail({ email: record.email });
      showToast('Password reset email sent', 'success');
    } catch (error) {
      console.error('Failed to send reset password email:', error);
      showToast(error?.response?.data?.message || 'Failed to send reset email', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddUser = async (values) => {
    const payload = {
      ...values,
      equipment_limit: values.equipment_limit,
      inspection_expiry_date: values.inspection_expiry_date?.format('YYYY-MM-DD')
    };

    setSubmitting(true);
    try {
      await createUser(payload);
      showToast('User created successfully', 'success');
      handleCreateCancel();
      fetchData(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error('Failed to create user:', error);
      showToast(error?.response?.data?.message || 'Failed to create user', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveAccess = async (values) => {
    if (!selectedUser?.enc_id) {
      showToast('User identifier is missing', 'error');
      return;
    }

    const payload = {
      no_of_equipment: values.equipment_limit,
      inspection_expiry_date: values.inspection_expiry_date?.format('YYYY-MM-DD')
    };

    setSubmitting(true);
    try {
      await updateUserAccess(selectedUser.enc_id, payload);
      showToast('User access updated successfully', 'success');
      handleAccessCancel();
      fetchData(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error('Failed to update user access:', error);
      showToast(
        error?.response?.data?.message ||
          'Failed to update user access. Please confirm the backend update route is available.',
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (record) => {
    try {
      await updateUserStatus(record.enc_id, record.to_status);
      const statusLabel = record.to_status === 0 ? 'disabled' : record.to_status === 1 ? 'enabled' : 'updated';
      showToast(`User ID ${record.id} ${statusLabel}`, 'success');
      fetchData(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error('Updating failed:', error);
      showToast('Failed to update user', 'error');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 70 },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    {
      title: 'Allowed Equipment',
      key: 'equipment_limit',
      render: (_, record) => getAllowedEquipment(record) ?? '—'
    },
    {
      title: 'Current Equipment',
      key: 'current_equipment_count',
      render: (_, record) => getCurrentEquipmentCount(record) ?? '—'
    },
    // {
    //   title: 'Remaining Slots',
    //   key: 'remaining_equipment_slots',
    //   render: (_, record) => getRemainingEquipmentSlots(record) ?? '—'
    // },
    {
      title: 'Inspection Expiry',
      key: 'inspection_expiry_date',
      render: (_, record) => formatDate(getInspectionExpiryDate(record))
    },
    {
      title: 'Expiry Status',
      key: 'expiry_status',
      render: (_, record) => {
        const status = getInspectionExpiryLabel(record);

        return <Tag color={status.color}>{status.label}</Tag>;
      }
    },
    {
      title: 'Created At (IST)',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (_, record) => formatToIST(record.created_at)
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        const status = getUserStatus(record);
        const nextStatus = status === 1 ? 0 : 1;
        const toggleLabel = nextStatus === 0 ? 'Disable' : 'Enable';

        return (
          <Space wrap>
            <Button type="link" onClick={() => openAccessModal(record)}>
              {getAllowedEquipment(record) || getInspectionExpiryDate(record) ? 'Edit Access' : 'Set Access'}
            </Button>
            <Button type="link" onClick={() => openViewProfileModal(record)}>
              View Profile
            </Button>
            <Button type="link" onClick={() => openEditProfileModal(record)}>
              Edit Profile
            </Button>
            <ModalConfirm
              btnText={toggleLabel}
              title={`Confirm ${toggleLabel}`}
              okType={nextStatus === 0 ? 'danger' : 'primary'}
              okText={toggleLabel}
              confirmText={`Are you sure you want to ${toggleLabel.toLowerCase()} User ID ${record.id}?`}
              data={{ to_status: nextStatus, ...record }}
              handleDelete={handleUpdate}
            />
          </Space>
        );
      }
    }
  ];

  return (
    <MainCard title="User List">
      <div style={{ marginBottom: 16, textAlign: 'right' }}>
        <Button type="primary" onClick={showAddModal}>
          Add New User
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        scroll={{ x: 'max-content' }}
      />

      <Modal title="Add New User" open={isCreateModalOpen} onCancel={handleCreateCancel} footer={null} destroyOnHidden>
        <Form form={createForm} layout="vertical" onFinish={handleAddUser}>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Please enter name' }]}>
            <Input />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Invalid email' }
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="password" label="Password" rules={[{ required: true, message: 'Please enter password' }]}>
            <Input.Password />
          </Form.Item>

          <Form.Item
            name="equipment_limit"
            label="Allowed Equipment Count"
            rules={[{ required: true, message: 'Please enter allowed equipment count' }]}
          >
            <InputNumber min={1} precision={0} style={{ width: '100%' }} placeholder="Enter number of equipment" />
          </Form.Item>

                  <Form.Item
            name="inspection_expiry_date"
            label="Inspection Expiry Date"
            rules={[{ required: true, message: 'Please select inspection expiry date' }]}
          >
            <DatePicker
  style={{ width: '100%' }}
  format="DD MMM YYYY"
  disabledDate={(current) => current && current < dayjs().startOf('day')}
  getPopupContainer={(trigger) => trigger.closest('.ant-modal-body') || document.body}
/>
          </Form.Item>

          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Expiry status is derived automatically: Active, Expiring Soon within 30 days, or Expired.
          </Typography.Text>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={submitting}>
                Create User
              </Button>
              <Button onClick={handleCreateCancel} disabled={submitting}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={selectedUser ? `Set Access for ${selectedUser.name}` : 'Set Access'}
        open={isAccessModalOpen}
        onCancel={handleAccessCancel}
        footer={null}
        destroyOnHidden
      >
        <Form form={accessForm} layout="vertical" onFinish={handleSaveAccess}>
          <Form.Item
            name="equipment_limit"
            label="Allowed Equipment Count"
            rules={[{ required: true, message: 'Please enter allowed equipment count' }]}
          >
            <InputNumber min={1} precision={0} style={{ width: '100%' }} placeholder="Enter number of equipment" />
          </Form.Item>

        

<Form.Item
  name="inspection_expiry_date"
  label="Inspection Expiry Date"
  rules={[{ required: true, message: 'Please select inspection expiry date' }]}
>
<DatePicker
  style={{ width: '100%' }}
  format="DD MMM YYYY"
  placement="bottom"
  popupStyle={{ zIndex: 2000 }}
  disabledDate={(current) => current && current < dayjs().startOf('day')}
  getPopupContainer={() => document.body}
/>
</Form.Item>

          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Use this to control how many equipment records the user can manage and when their inspection access expires.
          </Typography.Text>

          {selectedUser ? (
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
              Current usage: {getCurrentEquipmentCount(selectedUser) ?? 0} equipment
              {getRemainingEquipmentSlots(selectedUser) !== null && getRemainingEquipmentSlots(selectedUser) !== undefined
                ? ` • Remaining slots: ${getRemainingEquipmentSlots(selectedUser)}`
                : ''}
            </Typography.Text>
          ) : null}

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={submitting}>
                Save Access
              </Button>
              <Button onClick={handleAccessCancel} disabled={submitting}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={selectedProfileUser ? `View Profile - ${selectedProfileUser.name || selectedProfileUser.email || ''}` : 'View Profile'}
        open={isViewProfileModalOpen}
        onCancel={handleProfileCancel}
        footer={null}
        destroyOnHidden
      >
        {selectedProfileUser ? (
          <>
            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label="Email">{selectedProfileUser.email || '—'}</Descriptions.Item>
              <Descriptions.Item label="Phone">
                {getCountryCode(selectedProfileUser) ? `${getCountryCode(selectedProfileUser)} ` : ''}
                {getPhone(selectedProfileUser) || '—'}
              </Descriptions.Item>
              {!isAdminUser(selectedProfileUser) ? (
                <>
                  <Descriptions.Item label="Inspection Expiry Date">
                    {formatDate(getInspectionExpiryDate(selectedProfileUser)) || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Allowed Equipment">{getAllowedEquipment(selectedProfileUser) ?? '—'}</Descriptions.Item>
                  <Descriptions.Item label="Equipment Count">{getCurrentEquipmentCount(selectedProfileUser) ?? '—'}</Descriptions.Item>
                  <Descriptions.Item label="Support Email">{SUPPORT_EMAIL}</Descriptions.Item>
                </>
              ) : null}
            </Descriptions>

            <Divider />

            <Space>
              <Button onClick={() => handleResetPassword(selectedProfileUser)} loading={submitting}>
                Reset Password
              </Button>
              <Button onClick={handleProfileCancel}>Close</Button>
            </Space>
          </>
        ) : null}
      </Modal>

      <Modal
        title={selectedProfileUser ? `Edit Profile - ${selectedProfileUser.name || selectedProfileUser.email || ''}` : 'Edit Profile'}
        open={isEditProfileModalOpen}
        onCancel={handleProfileCancel}
        footer={null}
        destroyOnHidden
      >
        <Form
          form={profileForm}
          layout="vertical"
          onFinish={() => showToast('Profile update not available yet (backend API missing)', 'info')}
        >
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Invalid email' }
            ]}
          >
            <Input disabled={selectedProfileUser ? isAdminUser(selectedProfileUser) : false} />
          </Form.Item>

          <Form.Item name="country_code" label="Country Code">
            <Input disabled={selectedProfileUser ? isAdminUser(selectedProfileUser) : false} placeholder="+91" />
          </Form.Item>

          <Form.Item name="phone" label="Phone">
            <Input disabled={selectedProfileUser ? isAdminUser(selectedProfileUser) : false} />
          </Form.Item>

          <Space>
            <Button type="primary" htmlType="submit">
              Save
            </Button>
            <Button onClick={() => (selectedProfileUser ? handleResetPassword(selectedProfileUser) : null)} loading={submitting}>
              Reset Password
            </Button>
            <Button onClick={handleProfileCancel}>Close</Button>
          </Space>
        </Form>
      </Modal>
    </MainCard>
  );
}
